import React from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import { alpha, keyframes } from "@mui/material/styles";
import { Link } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CodeIcon from "@mui/icons-material/Code";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import ApiIcon from "@mui/icons-material/Api";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import StorefrontIcon from "@mui/icons-material/Storefront";
import HandshakeIcon from "@mui/icons-material/Handshake";
import EmailIcon from "@mui/icons-material/Email";
import Type from "./Type";
import mainImg from "../../Assets/main_img-optimized.jpg";

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-18px); }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(0, 127, 255, 0.4); }
  50% { box-shadow: 0 0 0 20px rgba(0, 127, 255, 0); }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const heroStats = [
  { value: "5+", label: "Years building production apps" },
  { value: "SaaS", label: "Dashboards, APIs, billing flows" },
  { value: "Web3", label: "Wallets, DApps, smart contracts" },
];

const serviceHighlights = [
  {
    title: "Enterprise SaaS Development",
    icon: <BusinessCenterIcon />,
    description:
      "MERN and Next.js platforms with clean API design, role-based access, admin workflows, and maintainable product architecture.",
  },
  {
    title: "Oracle Fusion API Integration",
    icon: <ApiIcon />,
    description:
      "Invoice, customer, and ERP data integrations for enterprise environments where reliability, auditability, and reconciliation matter.",
  },
  {
    title: "Invoicing & Accounting Systems",
    icon: <ReceiptLongIcon />,
    description:
      "Invoice lifecycle automation, GL structures, P&L reports, balance sheets, approvals, payments, and finance dashboards.",
  },
  {
    title: "React/Next.js Dashboards",
    icon: <DashboardCustomizeIcon />,
    description:
      "Responsive operational dashboards for SaaS teams, finance users, admins, and customer-facing portals.",
  },
  {
    title: "Web3 DApps & Wallet Integration",
    icon: <AccountBalanceWalletIcon />,
    description:
      "WalletConnect, MetaMask, Ethers.js, token flows, staking portals, and blockchain transaction interfaces.",
  },
];

const caseStudies = [
  {
    title: "Oracle Fusion Invoicing System",
    path: "/project/oracle-fusion-invoicing",
    description:
      "Enterprise invoicing workflows connected to Oracle Fusion APIs for invoice sync, status tracking, and finance operations.",
    tags: ["Oracle Fusion API", "Invoices", "ERP"],
  },
  {
    title: "Accounting Software",
    path: "/project/accounting-gl-pl-balance-sheet",
    description:
      "Finance modules covering GL, P&L, balance sheet views, invoice records, and reporting for business users.",
    tags: ["GL", "P&L", "Balance Sheet"],
  },
  {
    title: "SaaS Dashboard",
    path: "/project/saas-dashboard",
    description:
      "A responsive SaaS control center with admin workflows, analytics, account management, and operational visibility.",
    tags: ["React", "Next.js", "SaaS"],
  },
  {
    title: "Web3 DApp & Wallet Integration",
    path: "/project/web3-wallet-integration",
    description:
      "Wallet login, transaction flows, smart contract interaction, and user-friendly blockchain status handling.",
    tags: ["Web3", "Wallets", "DApp"],
  },
];

const ctaLinks = [
  {
    label: "Fiverr",
    href: "https://www.fiverr.com/rahmanidrees",
    icon: <StorefrontIcon />,
  },
  {
    label: "Upwork",
    href: "https://www.upwork.com/freelancers/rahmanidrees",
    icon: <BusinessCenterIcon />,
  },
  {
    label: "Project Consultation",
    href: "mailto:rahmanidrees11@gmail.com?subject=Project%20Consultation",
    icon: <HandshakeIcon />,
  },
  {
    label: "Contact",
    href: "mailto:rahmanidrees11@gmail.com",
    icon: <EmailIcon />,
  },
];

const floatingBadges = [
  { icon: <DashboardCustomizeIcon />, top: "5%", left: "-10%", delay: "0s" },
  { icon: <ApiIcon />, top: "20%", right: "-15%", delay: "0.5s" },
  { icon: <ReceiptLongIcon />, bottom: "15%", left: "-5%", delay: "1s" },
  { icon: <AccountBalanceWalletIcon />, bottom: "5%", right: "-10%", delay: "1.5s" },
];

function Home() {
  return (
    <>
      <Box
        component="section"
        id="home"
        aria-labelledby="home-heading"
        sx={{
          minHeight: { xs: "auto", md: "92vh" },
          display: "flex",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
          pt: { xs: 12, md: 14 },
          pb: { xs: 8, md: 10 },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            left: "-5%",
            width: "40%",
            height: "40%",
            background: "radial-gradient(circle, rgba(0, 127, 255, 0.15) 0%, transparent 70%)",
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "10%",
            right: "-5%",
            width: "35%",
            height: "35%",
            background: "radial-gradient(circle, rgba(0, 212, 170, 0.1) 0%, transparent 70%)",
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  animation: `${float} 6s ease-in-out infinite`,
                  animationDelay: "0.5s",
                }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 0.75,
                    mb: 3,
                    borderRadius: 10,
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  }}
                >
                  <CodeIcon sx={{ fontSize: 18, color: "primary.main" }} aria-hidden="true" />
                  <Typography
                    variant="body2"
                    component="p"
                    sx={{ color: "primary.main", fontWeight: 600 }}
                  >
                    Enterprise SaaS Developer | MERN Stack | Oracle Fusion API | Web3 DApps
                  </Typography>
                </Box>

                <Typography
                  id="home-heading"
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2.35rem", sm: "3rem", md: "3.55rem" },
                    fontWeight: 700,
                    lineHeight: 1.1,
                    mb: 2,
                    background: "linear-gradient(135deg, #ffffff 0%, #007FFF 50%, #00D4AA 100%)",
                    backgroundSize: "200% 200%",
                    animation: `${gradientShift} 5s ease infinite`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Enterprise SaaS, Oracle Fusion API and Web3 DApp Developer
                </Typography>

                <Typography
                  variant="h5"
                  component="p"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 400,
                    mb: 2,
                    maxWidth: 620,
                    lineHeight: 1.6,
                  }}
                >
                  I am{" "}
                  <Box component="span" sx={{ color: "primary.main", fontWeight: 600 }}>
                    Rahman Idrees
                  </Box>
                  , a MERN stack engineer building invoicing systems, accounting
                  platforms, React/Next.js dashboards, ERP integrations, and
                  wallet-connected Web3 products.
                </Typography>

                <Box sx={{ mb: 4, minHeight: 40 }}>
                  <Type />
                </Box>

                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    component={Link}
                    to="/project"
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ px: 4, py: 1.5, fontSize: "1rem" }}
                  >
                    View Case Studies
                  </Button>
                  <Button
                    component="a"
                    href="mailto:rahmanidrees11@gmail.com?subject=Project%20Consultation"
                    variant="outlined"
                    size="large"
                    sx={{ px: 4, py: 1.5, fontSize: "1rem" }}
                  >
                    Book Consultation
                  </Button>
                </Box>

                <Box sx={{ display: "flex", gap: 4, mt: 6, flexWrap: "wrap" }}>
                  {heroStats.map((stat) => (
                    <Box key={stat.label} sx={{ maxWidth: 170 }}>
                      <Typography
                        variant="h3"
                        component="p"
                        sx={{ fontWeight: 700, color: "primary.main", mb: 0.5 }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" component="p" sx={{ color: "text.secondary" }}>
                        {stat.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Box
                  sx={{
                    position: "relative",
                    width: { xs: 280, sm: 320, md: 380 },
                    height: { xs: 280, sm: 320, md: 380 },
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: -10,
                      left: -10,
                      right: -10,
                      bottom: -10,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #007FFF 0%, #00D4AA 50%, #007FFF 100%)",
                      backgroundSize: "200% 200%",
                      animation: `${gradientShift} 3s ease infinite, ${pulse} 2s ease-in-out infinite`,
                      opacity: 0.8,
                    }}
                  />

                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #1a1a2e 0%, #0a0a0f 100%)",
                      border: "4px solid rgba(255, 255, 255, 0.1)",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      animation: `${float} 6s ease-in-out infinite`,
                    }}
                  >
                    <Box
                      component="img"
                      src={mainImg}
                      alt="Illustrated Web3 and MERN engineering badge for Rahman Idrees"
                      loading="eager"
                      sx={{
                        width: "90%",
                        height: "90%",
                        objectFit: "cover",
                        borderRadius: "50%",
                      }}
                    />
                  </Box>

                  {floatingBadges.map(({ icon, delay, ...position }, index) => (
                    <Box
                      key={index}
                      aria-hidden="true"
                      sx={{
                        position: "absolute",
                        ...position,
                        width: 50,
                        height: 50,
                        borderRadius: 2,
                        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.8),
                        backdropFilter: "blur(10px)",
                        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "primary.main",
                        animation: `${float} 4s ease-in-out infinite`,
                        animationDelay: delay,
                        boxShadow: (theme) => `0 8px 32px ${alpha(theme.palette.common.black, 0.3)}`,
                      }}
                    >
                      {icon}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        component="section"
        aria-labelledby="services-heading"
        sx={{ py: { xs: 8, md: 10 }, position: "relative" }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography
              variant="overline"
              component="p"
              sx={{ color: "primary.main", fontWeight: 700, letterSpacing: 3, mb: 1 }}
            >
              Services
            </Typography>
            <Typography id="services-heading" variant="h2" sx={{ fontWeight: 700, mb: 2 }}>
              Development Services for Complex Product Work
            </Typography>
            <Typography
              variant="body1"
              component="p"
              color="text.secondary"
              sx={{ maxWidth: 760, mx: "auto" }}
            >
              I focus on systems where product UX, backend logic, financial data,
              integrations, and operational workflows need to work cleanly together.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {serviceHighlights.map((service) => (
              <Grid item xs={12} sm={6} md={4} key={service.title}>
                <Card sx={{ height: "100%" }}>
                  <CardContent sx={{ p: 3 }}>
                    <Avatar
                      sx={{
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                        color: "primary.main",
                        mb: 2,
                      }}
                    >
                      {service.icon}
                    </Avatar>
                    <Typography variant="h5" component="h3" sx={{ fontWeight: 700, mb: 1 }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body2" component="p" color="text.secondary">
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box
        component="section"
        aria-labelledby="case-studies-heading"
        sx={{ py: { xs: 8, md: 10 } }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography
              variant="overline"
              component="p"
              sx={{ color: "secondary.main", fontWeight: 700, letterSpacing: 3, mb: 1 }}
            >
              Case Studies
            </Typography>
            <Typography id="case-studies-heading" variant="h2" sx={{ fontWeight: 700, mb: 2 }}>
              Selected Work Areas
            </Typography>
            <Typography
              variant="body1"
              component="p"
              color="text.secondary"
              sx={{ maxWidth: 720, mx: "auto" }}
            >
              These summaries avoid private client names while still showing the
              systems, integrations, and business workflows I can deliver.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {caseStudies.map((study) => (
              <Grid item xs={12} md={6} key={study.title}>
                <Card sx={{ height: "100%" }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                      {study.tags.map((tag) => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                      ))}
                    </Box>
                    <Typography variant="h5" component="h3" sx={{ fontWeight: 700, mb: 1 }}>
                      {study.title}
                    </Typography>
                    <Typography variant="body2" component="p" color="text.secondary" sx={{ mb: 2 }}>
                      {study.description}
                    </Typography>
                    <Button
                      component={Link}
                      to={study.path}
                      size="small"
                      endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                    >
                      Read Case Study
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" aria-labelledby="cta-heading" sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 3,
              backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.45),
              border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              textAlign: "center",
            }}
          >
            <Typography id="cta-heading" variant="h2" sx={{ fontWeight: 700, mb: 2 }}>
              Need a SaaS, ERP, Accounting, or Web3 Build?
            </Typography>
            <Typography
              variant="body1"
              component="p"
              color="text.secondary"
              sx={{ maxWidth: 760, mx: "auto", mb: 4 }}
            >
              Send the scope, product goals, current stack, and any integration
              requirements. I can help shape the technical plan before development starts.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
              {ctaLinks.map((cta) => (
                <Button
                  key={cta.label}
                  component="a"
                  href={cta.href}
                  target={cta.href.startsWith("mailto:") ? undefined : "_blank"}
                  rel={cta.href.startsWith("mailto:") ? undefined : "noopener noreferrer"}
                  variant={cta.label === "Project Consultation" ? "contained" : "outlined"}
                  startIcon={cta.icon}
                  sx={{ minWidth: { xs: "100%", sm: 180 } }}
                >
                  {cta.label}
                </Button>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
}

export default Home;
